<template>
  <v-card class="elevation-4 mt-12 pa-sm-4">
    <template v-if="isEmailExpired">
      <SelfInviteView :user-email="email" @backToLogin="clickBackToInit()" />
    </template>
    <template v-else>
      <v-card-title>
        <div class="title text-center">{{ titleText }}</div>
      </v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="valid" lazy-validation class="set-login" @submit.prevent="submit">
          <v-alert v-if="errorText" type="error">{{ errorText }}</v-alert>
          <v-text-field
            v-if="![states.confirm, states.forcePasswordReset].includes(state)"
            v-model.trim="email"
            :label="$t('email')"
            :rules="emailRules"
            required
            name="email"
            validate-on-blur
            type="email"
            prepend-icon="mdi-account"
            :readonly="readOnlyEmail"
          />
          <v-text-field
            v-if="state === states.resetPasswordLogin && showPasswordResetCode"
            id="passwordResetCode"
            v-model="passwordResetCode"
            :rules="passwordResetCodeRules"
            :label="$t('passwordResetCode')"
            name="passwordResetCode"
            type="text"
            prepend-icon="mdi-email-alert-outline"
          />
          <v-text-field
            v-if="[states.resetPasswordLogin, states.login, states.register, states.newlyInvited, states.setMyPassword].includes(state)"
            id="password"
            :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            :rules="passwordRules"
            validate-on-blur
            required
            :label="state === states.register ? $t('createPassword') : $t('password')"
            name="password"
            autocomplete="off"
            @click:append="showPassword = !showPassword"
            prepend-icon="mdi-lock-check"
          />
          <v-text-field
            v-if="[states.resetPasswordLogin, states.register, states.setMyPassword].includes(state)"
            id="confirmPassword"
            :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
            v-model="confirmPassword"
            :type="showConfirmPassword ? 'text' : 'password'"
            :rules="confirmPasswordRule"
            validate-on-blur
            required
            :label="$t('confirmPassword')"
            name="confirmPassword"
            @click:append="showConfirmPassword = !showConfirmPassword"
            autocomplete="off"
            prepend-icon="mdi-lock-plus"
          />
          <div class="d-flex">
            <v-spacer />
            <v-btn
              v-if="[states.resetPasswordLogin, states.login].includes(state)"
              color="primary"
              text
              @click.prevent="gotoForgotPassword"
              >{{ $t("forgotPassword") }}</v-btn
            >
          </div>
          <v-alert v-if="successText" type="success">{{ successText }}</v-alert>
          <v-btn
            v-if="![states.confirm, states.forcePasswordReset].includes(state)"
            class="mt-4"
            type="submit"
            color="info"
            block
            :loading="loading"
            :disabled="loading || disableResetButton"
            >{{ loginText }}</v-btn
          >
          <div v-if="[states.login].includes(state) && checkValidEmailForAzure" class="mt-4">
            <v-btn class="mt-4" block color="info" href="#" @click.prevent="signInWithAzure()">
              <v-icon>mdi-microsoft-azure</v-icon>
              {{ $t("signInWithAzure") }}</v-btn
            >
          </div>
          <div class="d-flex">
            <v-spacer />
            <v-btn
              v-if="![states.init, states.forcePasswordReset].includes(state)"
              class="mt-4"
              color="primary"
              text
              href="#"
              @click.prevent="confirmationLabel === 'signUp' ? clickSignup() : clickBackToInit()"
              >{{ $t(confirmationLabel) }}</v-btn
            >
          </div>
        </v-form>

        <!-- <div v-if="state === states.init" class="mt-12">
          <h2 class="text-center mb-2 subtitle-1">{{ $t("newToOrbit") }}</h2>
          <v-btn color="primary" outlined block @click.prevent="clickSignup">{{ $t("signUp") }}</v-btn>
        </div> -->
      </v-card-text>
    </template>
  </v-card>
</template>

<script>
import { enc, SHA1 } from "crypto-js";
import Auth from "@aws-amplify/auth";
import SelfInviteView from "./SelfInviteView.vue";
import msalConfig from "../msal-config";
import SSOPartnersToDelaval from "../ssoPartnersToDelaval";

const states = {
  init: "init",
  login: "login",
  register: "register",
  resetPassword: "resetPassword",
  resetPasswordLogin: "resetPasswordLogin",
  setMyPassword: "setMyPassword",
  newlyInvited: "newlyInvited",
  confirm: "confirm",
  forcePasswordReset: "forcePasswordReset",
};
const emailInUrl = new URL(window.location.href).searchParams.get("email");
const newlyInvitedInUrl = new URL(window.location.href).searchParams.get("newlyInvited");
const isEmailVerifiedInUrl = new URL(window.location.href).searchParams.get("isEmailVerified");
const isCodeValidInUrl = new URL(window.location.href).searchParams.get("isCodeValid");
const codeInUrl = new URL(window.location.href).searchParams.get("confirmcode");
const tokenInUrl = new URL(window.location.href).searchParams.get("token");
const forcePasswordResetInUrl = new URL(window.location.href).searchParams.get("forcePasswordReset");

const initialState = () => {
  if (forcePasswordResetInUrl && emailInUrl) {
    return states.forcePasswordReset;
  }
  if (emailInUrl && (tokenInUrl || codeInUrl)) {
    return newlyInvitedInUrl === "true"
      ? states.newlyInvited
      : codeInUrl && isEmailVerifiedInUrl === "true" && isCodeValidInUrl === "true"
      ? states.setMyPassword
      : states.resetPasswordLogin;
  } else if (
    (isEmailVerifiedInUrl === "true" && isCodeValidInUrl === "false") ||
    (isEmailVerifiedInUrl === "false" && isCodeValidInUrl === "false")
  ) {
    return states.confirm;
  } else if (emailInUrl) {
    return states.login;
  }
  return states.init;
};

export default {
  components: { SelfInviteView },
  props: { redirect: { type: Function, default: function() {} } },
  data() {
    return {
      state: initialState(),
      states,
      email: emailInUrl || "",
      isEmailExpired: false,
      readOnlyEmail: emailInUrl ? true : false,
      // Removed Email validation for Service Account
      emailRules: [v => !!v || this.$t("emailRequired")],
      password: "",
      confirmPassword: "",
      confirmPasswordRule: [v => v === this.password || this.$t("confirmPasswordRules")],
      passwordRules: [
        v => !!v || this.$t("passwordRules.required"),
        v => this.isValidPassword(v) || this.$t("passwordRules.allowedPassword"),
        // v => v.length >= 8 || this.$t("passwordRules.minlength"),
        // v => v.length <= 16 || this.$t("passwordRules.maxlength"),
        // v => /[A-Z]/.test(v) || this.$t("passwordRules.uppercase"),
        // v => /[a-z]/.test(v) || this.$t("passwordRules.lowercase"),
        // v => /^\S*$/.test(v) || this.$t("passwordRules.spacecase"),
        // v => /\d/.test(v) || this.$t("passwordRules.digit"),
        // v => !!v.split("").find(char => "\\(){}[]:;\"'<,.?/|~!@#$%^&*_-+=`>".includes(char)) || this.$t("passwordRules.special"),
      ],
      passwordResetCode: codeInUrl || "",
      showPassword: false,
      showConfirmPassword: false,
      showPasswordResetCode: !codeInUrl,
      passwordResetCodeRules: [v => !!v || this.$t("passwordResetCodeRequired")],
      successText: "",
      errorText: "",
      loading: false,
      valid: true,
      confirmationLabel: "back",
    };
  },
  computed: {
    loginText: function() {
      const translationForState = {
        init: "continue",
        login: "signIn",
        register: "register",
        resetPassword: "resetMyPassword",
        resetPasswordLogin: "resetMyPassword",
        newlyInvited: "setMyPassword",
        setMyPassword: "setMyPassword",
      };
      return this.$t(translationForState[this.state]);
    },
    titleText: function() {
      const translationForState = {
        init: "logInToYourAccount",
        login: "logInToYourAccount",
        register: "signUpForYourAccount",
        resetPassword: "resetMyPassword",
        resetPasswordLogin: "resetMyPassword",
        newlyInvited: "setMyPassword",
        setMyPassword: "setMyPassword",
        confirm: "confirmText",
      };
      return this.$t(translationForState[this.state]);
    },
    // Disable button if password & confirm password are not valid
    disableResetButton: function() {
      return [states.resetPasswordLogin, states.setMyPassword].includes(this.state)
        ? ([states.resetPasswordLogin].includes(this.state) && !this.passwordResetCode) ||
            !this.password ||
            this.password != this.confirmPassword ||
            !this.isValidPassword()
        : false;
    },
    checkValidEmailForAzure() {
      return (
        [
          "niklas.billman17@gmail.com",
          "migtestfarmerdev@mailinator.com",
          "migtestfarmworkerdev@mailinator.com",
          "migtestdealerdev@mailinator.com",
          "migtestdwdev@mailinator.com",
        ].includes(this.email.toLowerCase()) && window.env.DEFAULT_REDIRECT.includes("dev")
      );
    },
  },
  async created() {
    if (this.state === states.forcePasswordReset) {
      await Auth.forgotPassword(this.transformEmail());
      this.successText = this.$t("resetLinkSentToYourEmailForced");
    } else if (this.state === states.confirm) {
      if (isEmailVerifiedInUrl === "true" && isCodeValidInUrl === "false") {
        this.confirmationLabel = "signIn";
        this.successText = this.$t("emailIsVerified");
      }
      if (isEmailVerifiedInUrl === "false" && isCodeValidInUrl === "false") {
        this.confirmationLabel = "signUp";
        this.errorText = this.$t("emailNotVerified");
      }
    } else {
      this.$nextTick(() => {
        let focusField = "email";
        if (this.state === states.newlyInvited || this.state === states.setMyPassword) {
          focusField = "password";
        }
        this.$refs.form.inputs.find(item => item.$attrs.name === focusField).focus();
      });
    }
  },
  mounted() {
    if (window.env.DEFAULT_REDIRECT.includes("dev")) {
      let recaptchaScript = document.createElement("script");
      recaptchaScript.setAttribute("src", "https://unpkg.com/@azure/msal-browser@2.37.0/lib/msal-browser.min.js");
      document.head.appendChild(recaptchaScript);
    }
  },
  methods: {
    isDelaval: function() {
      return this.email.split("@")[1].toLowerCase() === "delaval.com";
    },
    isPartnerToDelaval: function() {
      const partners = SSOPartnersToDelaval;
      return partners.includes(this.email.split("@")[1].toLowerCase());
    },
    clickBackToInit: function() {
      this.state = states.init;
      this.readOnlyEmail = false;
      this.password = "";
      this.passwordResetCode = "";
      this.successText = "";
      this.errorText = "";
      this.loading = false;
      this.valid = true;
      window.history.pushState({}, document.title, window.location.pathname);
      window.location.reload();
    },
    gotoForgotPassword: function() {
      this.state = states.resetPassword;
    },
    clickSignup: function() {
      this.errorText = "";
      this.confirmationLabel = "back";
      this.state = states.register;
    },
    async handleError(exception, defaultError) {
      const mappedErrors = {
        "User does not exist.": this.$t("errors.userDoesNotExist"),
        "Incorrect username or password.": this.$t("errors.incorrectUsernameOrPassword"),
        "User is not confirmed.": this.$t("errors.userIsNotConfirmed"),
        "Username/client id combination not found.": this.$t("errors.userDoesNotExist"),
      };
      if (exception.code === "CodeMismatchException") {
        this.errorText = [states.setMyPassword].includes(this.state)
          ? this.$t("errors.invalidOrExpiredLink")
          : this.$t("errors.invalidPasswordResetCode");
      } else if (exception.__type === "InvalidParameterException" || exception.code === "ExpiredCodeException") {
        if (exception.message.includes("Invalid code provided")) {
          this.errorText = this.$t("errors.invalidOrExpiredLinkWithNewCode");
          if ([states.setMyPassword].includes(this.state)) {
            this.isEmailExpired = true;
          }
        } else {
          this.errorText = exception.message;
        }
      } else if (exception.code === "InvalidPasswordException" || exception.__type === "InvalidPasswordException") {
        this.errorText = exception.message || this.$t("errors.invalidPasswordPolicy");
      } else {
        this.errorText = mappedErrors[exception.message] || this.$t(`errors.default.${defaultError}`);
      }
    },
    register: async function() {
      this.loading = true;
      const awsuser = {
        username: this.transformEmail(),
        password: this.password,
        attributes: {
          email: this.transformEmail(),
        },
      };

      try {
        await Auth.signUp(awsuser);
        this.successText = this.$t("aVerificationLinkHaveBeenSentToYourEmail");
        this.state = states.confirm;
      } catch (e) {
        this.errorText = e.message;
      }
      this.loading = false;
    },
    signIn: async function() {
      this.loading = true;

      const shaArray = SHA1(this.transformEmail(), this.password);
      const authInfo = {
        username: this.transformEmail(),
        password: this.password,
        validationData: { hashKey: enc.Base64.stringify(shaArray) },
      };
      try {
        await Auth.signIn(authInfo);
        this.redirect();
      } catch (e) {
        this.handleError(e, "signIn");
        this.loading = false;
      }
    },
    submit: async function() {
      this.successText = "";
      this.errorText = "";
      if (this.isDelaval() || this.isPartnerToDelaval()) {
        const provider = this.isDelaval() ? window.env.Provider.Delaval : window.env.Provider.External;

        Auth.federatedSignIn({ provider });
        this.loading = true;
        return;
      }
      switch (this.state) {
        case "init":
          let isValidEmail = true;
          try {
            const isValid = this.$refs.form.validate();
            if (!isValid) {
              return;
            }
            await Auth.signIn({ username: this.transformEmail(), password: "a" });
            this.state = states.login;
          } catch (e) {
            isValidEmail = false;
            if (e.code === "UserNotFoundException") {
              this.errorText = this.$t(`emailIncorrect`);
            } else {
              this.state = states.login;
            }
          }
          if (isValidEmail) this.focusPassword();
          break;
        case "login":
          this.$refs.form.validate();
          if (this.valid) {
            await this.signIn();
          }
          break;
        case "register":
          this.$refs.form.validate();
          if (this.valid) {
            await this.register();
          }
          break;
        case "resetPassword":
          this.$refs.form.validate();
          if (this.valid) {
            try {
              await Auth.forgotPassword(this.transformEmail());
              this.password = "";
              this.passwordResetCode = "";
              this.successText = this.$t("resetLinkSentToYourEmail");
              this.state = states.resetPasswordLogin;
            } catch (e) {
              this.handleError(e, "resetPassword");
              this.loading = false;
            }
          }
          break;
        case "resetPasswordLogin":
        case "newlyInvited":
        case "setMyPassword":
          this.$refs.form.validate();
          if (this.valid) {
            this.loading = true;
            try {
              await Auth.forgotPasswordSubmit(this.transformEmail(), this.passwordResetCode, this.password);
              const state = this.state;
              this.state = states.login;

              // this.clickBackToInit();
              this.successText = state === "setMyPassword" ? this.$t("setPasswordSuccess") : "";
              await this.signIn();
              this.loading = false;
              // this.redirect();
              setTimeout(() => {
                this.successText = "";
              }, 3000);
            } catch (e) {
              this.handleError(e, "resetPasswordLogin");
              this.loading = false;
            }
          }
          break;
      }
    },
    isValidPassword(secretCode) {
      return /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[!@#?$%^&*._-]).{8,16}$/.test(secretCode || this.password);
    },
    signInWithAzure() {
      let client = new msal.PublicClientApplication(msalConfig);
      const loginRequest = {
        scopes: ["openid"],
      };
      client
        .loginRedirect(loginRequest)
        .then()
        .catch();
    },
    focusPassword: function() {
      this.$nextTick(() => {
        this.$refs.form.inputs.find(item => item.$attrs.name === "password").focus();
      });
    },
    transformEmail() {
      return this.email.toLowerCase();
    },
  },
};
</script>

<style>
.set-login input[readonly] {
  background: #d9d9d9 !important;
  padding-left: 2px !important;
  margin-top: 2px !important;
  border: 0 !important;
}
.error--text .v-messages__message {
  line-height: 15px;
}
.d-flex .font11 {
  font-size: 11px !important;
}
.set-login .terms-checkbox label {
  display: -webkit-inline-box !important;
}
.set-login .terms-checkbox a {
  color: #1192e8;
  text-decoration: none;
}
.font16 {
  font-size: 16px;
}
@media only screen and (max-width: 600px) {
  .v-application .container .elevation-4 {
    box-shadow: none !important;
  }
}
</style>
